import { useState, type FormEvent } from 'react'
import { useAuth } from '@/lib/auth'
import { api, ApiError } from '@/lib/api'

export function SignIn() {
  const { signIn, error } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    try {
      await signIn(username, password)
    } catch {
      // The provider holds the message; a failed sign-in is not exceptional
      // here and must not blank the form.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin-page">
      <form className="signin-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-mark">MWL</span>
          <span className="brand-sub">Worklist Console</span>
        </div>

        <label className="field">
          <span className="field-label">Username</span>
          <input
            value={username}
            autoFocus
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Password</span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <div className="notice notice-error">{error}</div>}

        <button className="btn btn-primary" type="submit" disabled={busy || !username}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="hint">
          This console configures the Modality Worklist server. It holds patient
          scheduling data — treat it as you would the archive.
        </p>
      </form>
    </div>
  )
}

/**
 * Shown when the account owes a password change, which is every account an
 * administrator created.
 *
 * The whole app is replaced by this rather than it being a page inside the
 * console, because the server refuses everything else until the change is made
 * — so a console rendered behind it would be a console where every panel
 * failed.
 */
export function ForcePasswordChange() {
  const { user, refresh, signOut } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (next !== repeat) {
      setError('The new passwords do not match.')
      return
    }
    setBusy(true)
    try {
      await api.post('/api/v1/auth/password', {
        current_password: current,
        new_password: next,
      })
      // Changing a password ends every session for the account, including this
      // one. Signing back in is the honest next step, and refresh() will find
      // no session and drop to the login form.
      await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin-page">
      <form className="signin-card" onSubmit={submit}>
        <div className="brand">
          <span className="brand-mark">MWL</span>
          <span className="brand-sub">Worklist Console</span>
        </div>

        <div className="notice notice-warn">
          <strong>Set a new password.</strong> The one you signed in with was
          chosen by somebody else, so it is temporary. Nothing else is available
          until this is done.
        </div>

        <p className="hint">Signed in as {user?.username}.</p>

        <label className="field">
          <span className="field-label">Current password</span>
          <input
            type="password"
            value={current}
            autoComplete="current-password"
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">New password</span>
          <input
            type="password"
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
          />
          <span className="field-hint">At least 12 characters.</span>
        </label>

        <label className="field">
          <span className="field-label">Repeat new password</span>
          <input
            type="password"
            value={repeat}
            autoComplete="new-password"
            onChange={(e) => setRepeat(e.target.value)}
          />
        </label>

        {error && <div className="notice notice-error">{error}</div>}

        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || !current || next.length < 12}
        >
          {busy ? 'Saving…' : 'Set password'}
        </button>
        <button className="btn btn-small" type="button" onClick={() => void signOut()}>
          Sign out instead
        </button>
      </form>
    </div>
  )
}
